import React, { useState, useEffect } from "react";
import { Dialog } from "../ui/dialog";
import { RealTimeCharacterCounter } from "./real-time-character-counter";
import { RealTimePreview } from "./real-time-preview";
import { validateStartMessageForm } from "../../utils/start-message-validation";
import type {
  StartMessage,
  CreateStartMessageInput,
  UpdateStartMessageInput,
  StartMessageFormData,
} from "../../types/start-message-types";

interface StartMessageFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: CreateStartMessageInput | UpdateStartMessageInput
  ) => Promise<void>;
  startMessage?: StartMessage | null;
  title?: string;
}

export function StartMessageFormModal({
  isOpen,
  onClose,
  onSubmit,
  startMessage = null,
  title,
}: StartMessageFormModalProps): React.ReactElement {
  // Form state
  const [formData, setFormData] = useState<StartMessageFormData>({
    name: "",
    content: "",
    tags: [],
    isActive: true,
  });

  const [tagInput, setTagInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [validationWarnings, setValidationWarnings] = useState<
    Record<string, string[]>
  >({});

  // Initialize form data when modal opens or startMessage changes
  useEffect(() => {
    if (isOpen) {
      if (startMessage) {
        setFormData({
          name: startMessage.name,
          content: startMessage.content,
          tags: [...(startMessage.tags || [])],
          isActive: startMessage.isActive,
        });
      } else {
        setFormData({
          name: "",
          content: "",
          tags: [],
          isActive: true,
        });
      }
      setTagInput("");
      setValidationErrors({});
      setValidationWarnings({});
      setShowPreview(false);
    }
  }, [isOpen, startMessage]);

  // Real-time validation
  useEffect(() => {
    const validation = validateStartMessageForm(formData);
    setValidationErrors(validation.errors);
    setValidationWarnings(validation.warnings);
  }, [formData]);

  // Handle form field changes
  const handleInputChange = (field: keyof StartMessageFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle tag input
  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      handleInputChange("tags", [...formData.tags, tag]);
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    handleInputChange(
      "tags",
      formData.tags.filter((tag) => tag !== tagToRemove)
    );
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateStartMessageForm(formData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        name: formData.name.trim(),
        content: formData.content.trim(),
        tags: formData.tags.length > 0 ? formData.tags : undefined,
        isActive: formData.isActive,
      };

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error("Error submitting start message:", error);
      // Handle error (could show toast notification)
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalTitle =
    title || (startMessage ? "Edit Start Message" : "Create Start Message");

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={modalTitle} size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Form Fields */}
        <div className="space-y-4">
          {/* Name Field */}
          <div>
            <label
              htmlFor="start-message-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Message Name *
            </label>
            <input
              type="text"
              id="start-message-name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                validationErrors.name ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="e.g., Welcome Message, Product Demo"
              maxLength={100}
            />
            {validationErrors.name && (
              <div className="mt-1 text-sm text-red-600">
                {validationErrors.name.map((error, index) => (
                  <p key={index}>{error}</p>
                ))}
              </div>
            )}
          </div>

          {/* Content Field */}
          <div>
            <label
              htmlFor="start-message-content"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Message Content *
            </label>
            <div className="relative">
              <textarea
                id="start-message-content"
                value={formData.content}
                onChange={(e) => handleInputChange("content", e.target.value)}
                className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none ${
                  validationErrors.content
                    ? "border-red-300"
                    : "border-gray-300"
                }`}
                rows={4}
                placeholder="Enter your start message (max 140 characters + 20 for tracking)"
              />
            </div>

            {/* Real-time Character Counter */}
            <div className="mt-2">
              <RealTimeCharacterCounter
                content={formData.content}
                onContentChange={(content) =>
                  handleInputChange("content", content)
                }
                enableRealTimeUpdates={true}
                updateInterval={100}
              />
            </div>

            {validationErrors.content && (
              <div className="mt-1 text-sm text-red-600">
                {validationErrors.content.map((error, index) => (
                  <p key={index}>{error}</p>
                ))}
              </div>
            )}

            {validationWarnings.content && (
              <div className="mt-1 text-sm text-orange-600">
                {validationWarnings.content.map((warning, index) => (
                  <p key={index}>{warning}</p>
                ))}
              </div>
            )}
          </div>

          {/* Tags Field */}
          <div>
            <label
              htmlFor="start-message-tags"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Tags (Optional)
            </label>
            <div className="space-y-2">
              <div className="flex">
                <input
                  type="text"
                  id="start-message-tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  className="flex-1 border border-gray-300 rounded-l-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Add tags (press Enter or comma to add)"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="bg-purple-600 text-white px-4 py-2 rounded-r-lg hover:bg-purple-700"
                >
                  Add
                </button>
              </div>

              {/* Tag Display */}
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-sm rounded-md"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-purple-600 hover:text-purple-800"
                      >
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {validationErrors.tags && (
              <div className="mt-1 text-sm text-red-600">
                {validationErrors.tags.map((error, index) => (
                  <p key={index}>{error}</p>
                ))}
              </div>
            )}
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="start-message-active"
              checked={formData.isActive}
              onChange={(e) => handleInputChange("isActive", e.target.checked)}
              className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500"
            />
            <label
              htmlFor="start-message-active"
              className="ml-2 text-sm text-gray-700"
            >
              Active (available for use in conversations)
            </label>
          </div>
        </div>

        {/* Preview Toggle */}
        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center text-sm text-purple-600 hover:text-purple-700"
          >
            <svg
              className={`w-4 h-4 mr-1 transition-transform ${
                showPreview ? "rotate-90" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            {showPreview ? "Hide Preview" : "Show Preview"}
          </button>
        </div>

        {/* Real-time Preview Section */}
        {showPreview && (
          <div className="border-t pt-4">
            <RealTimePreview
              content={formData.content}
              messageId={startMessage?.id}
              conversationId="sample-conversation-id"
              userId="sample-user-id"
              enableRealTimeUpdates={true}
              updateInterval={500}
            />
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting || Object.keys(validationErrors).length > 0}
          >
            {isSubmitting
              ? "Saving..."
              : startMessage
              ? "Update Message"
              : "Create Message"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
